import { useEffect, useState } from "react";
import { MessagesSquare, ScrollText, MessageCircleReply, HeartHandshake, Swords, Copy, Loader2, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Simulator from "@/components/Simulator";

const TOOLS = [
  { id: "starters", label: "Conversation starters", icon: MessagesSquare },
  { id: "script", label: "Script builder", icon: ScrollText },
  { id: "reply", label: "Reply coach", icon: MessageCircleReply },
  { id: "repair", label: "Apology & repair", icon: HeartHandshake },
  { id: "simulator", label: "Conflict simulator", icon: Swords },
];

function CopyBtn({ text }) {
  return (
    <button className="btn btn-ghost px-2" onClick={async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Press Ctrl/Cmd+C"); } }} aria-label="Copy"><Copy size={15} /></button>
  );
}

function CoachResult({ mode, result }) {
  if (!result) return null;
  return (
    <div className="dk-card p-6 space-y-4 anim-in" data-testid="coach-result">
      {result.safety_notice && (
        <div className="rounded-xl p-4 text-sm" style={{ background: "var(--sage-tint)" }}>{result.safety_notice}</div>
      )}
      {mode === "starters" && (result.items || []).map((it, i) => (
        <div key={i} className="rounded-xl p-4 flex items-start justify-between gap-3" style={{ background: "var(--card-alt)" }}>
          <div><p className="font-serif text-[17px]">“{it.opener}”</p><p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{it.why}</p></div>
          <CopyBtn text={it.opener} />
        </div>
      ))}
      {mode === "script" && (
        <div className="space-y-4">
          {result.opening && <div className="rounded-xl p-4" style={{ background: "var(--card-alt)" }}><span className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Opening</span><p className="font-serif text-[17px] mt-1">“{result.opening}”</p></div>}
          {(result.steps || []).map((s, i) => (
            <div key={i} className="flex gap-3">
              <span className="grid place-items-center w-7 h-7 rounded-full shrink-0" style={{ background: "var(--sage-tint)", color: "var(--sage)" }}>{i + 1}</span>
              <div><p className="font-sans font-semibold">{s.title}</p><p className="text-sm">“{s.line}”</p></div>
            </div>
          ))}
          {result.tips?.length > 0 && <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Tips</p><ul className="text-sm list-disc pl-5 space-y-1">{result.tips.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          {result.avoid?.length > 0 && <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--error)" }}>Gently avoid</p><ul className="text-sm list-disc pl-5 space-y-1">{result.avoid.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
        </div>
      )}
      {mode === "reply" && (
        <div className="space-y-3">
          <div className="rounded-xl p-4 flex items-start justify-between gap-3" style={{ background: "var(--card-alt)" }}>
            <p className="font-serif text-[17px]">“{result.reply}”</p><CopyBtn text={result.reply} />
          </div>
          <Kv label="Why it works" value={result.why} />
          <Kv label="Tone" value={result.tone} />
          <Kv label="Gently avoid" value={result.avoid} error />
        </div>
      )}
      {mode === "repair" && (
        <div className="space-y-3">
          <Kv label="Acknowledge" value={result.acknowledge} />
          <div className="rounded-xl p-4 flex items-start justify-between gap-3" style={{ background: "var(--card-alt)" }}>
            <div><span className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>An apology you could say</span><p className="font-serif text-[17px] mt-1">“{result.apology}”</p></div><CopyBtn text={result.apology} />
          </div>
          {result.steps?.length > 0 && <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Repair steps</p><ul className="text-sm list-disc pl-5 space-y-1">{result.steps.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          <Kv label="Reconnect" value={result.reconnect} />
        </div>
      )}
    </div>
  );
}
const Kv = ({ label, value, error }) => value ? (
  <div><p className="text-xs uppercase tracking-wide mb-1" style={{ color: error ? "var(--error)" : "var(--text-muted)" }}>{label}</p><p className="text-[15px]">{value}</p></div>
) : null;

const FIELDS = {
  starters: [{ k: "topic", label: "What would you like to talk about?", ph: "e.g. screen time, a new friend, exam stress", area: true }],
  script: [{ k: "situation", label: "What's the situation?", ph: "e.g. grades dropped and every reminder turns into a fight", area: true }, { k: "goal", label: "What do you hope for?", ph: "e.g. agree a homework routine without shouting" }],
  reply: [{ k: "they_said", label: "What did your child say or text?", ph: "e.g. “leave me alone, you never get it”", area: true }, { k: "you_want", label: "What do you want to happen?", ph: "e.g. stay calm and keep the door open" }],
  repair: [{ k: "what_happened", label: "What happened?", ph: "e.g. I lost my temper and said something I regret", area: true }],
};

export default function Coach() {
  const [tab, setTab] = useState("starters");
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/children").then(({ data }) => { setChildren(data.children); if (data.children[0]) setChild(data.children[0]); }).catch(() => {}); }, []);
  useEffect(() => { setResult(null); setFields({}); }, [tab]);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/coach", { mode: tab, fields, child_id: child?.id || null });
      setResult(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const label = (c) => c ? `${c.nickname || "Child"} · ${c.age_band}` : "No specific child";

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-8 items-start" data-testid="coach-page">
      <aside className="lg:sticky lg:top-24 space-y-4">
        <div>
          <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">Coach</h1>
          <p style={{ color: "var(--text-muted)" }}>Find the calm, caring words — and practise the hard talks safely.</p>
        </div>
        {children.length > 0 && (
          <div className="relative">
            <button className="btn btn-outline w-full justify-between" onClick={() => setOpen(!open)} data-testid="coach-child-switcher">{label(child)} <ChevronDown size={16} /></button>
            {open && (
              <div className="absolute left-0 right-0 mt-2 dk-card p-2 z-30 anim-in">
                <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm" onClick={() => { setChild(null); setOpen(false); }}>No specific child</button>
                {children.map((c) => <button key={c.id} className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm" onClick={() => { setChild(c); setOpen(false); }}>{label(c)}</button>)}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`coach-tab-${t.id}`} className={`btn justify-start ${tab === t.id ? "btn-secondary" : "btn-outline"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </aside>

      <div className="space-y-4">
      {tab === "simulator" ? (
        <Simulator child={child} />
      ) : (
        <>
          <div className="dk-raised rounded-2xl p-5 border space-y-4" data-testid="coach-form">
            {FIELDS[tab].map((f) => (
              <div key={f.k}>
                <label className="text-sm font-medium">{f.label}</label>
                {f.area
                  ? <textarea className="dk-input mt-1 resize-none" rows={3} value={fields[f.k] || ""} maxLength={400} placeholder={f.ph} onChange={(e) => setFields({ ...fields, [f.k]: e.target.value })} data-testid={`coach-field-${f.k}`} />
                  : <input className="dk-input mt-1" value={fields[f.k] || ""} maxLength={300} placeholder={f.ph} onChange={(e) => setFields({ ...fields, [f.k]: e.target.value })} data-testid={`coach-field-${f.k}`} />}
              </div>
            ))}
            <div className="flex justify-end">
              <button className="btn btn-primary" disabled={loading} onClick={run} data-testid="coach-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {loading ? "Thinking…" : "Get guidance"}
              </button>
            </div>
          </div>
          {loading && <div className="dk-card p-6 space-y-3 anim-in"><div className="h-4 w-full rounded" style={{ background: "var(--card-alt)" }} /><div className="h-4 w-2/3 rounded" style={{ background: "var(--card-alt)" }} /></div>}
          <CoachResult mode={tab} result={result} />
        </>
      )}
      </div>
    </div>
  );
}
